import { useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import ThemeToggle from '@/components/ThemeToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const USERS = ['Admin', 'Djohan', 'Soffian', 'Yahya', 'Isa', 'Elfee'];

export default function Login({ onLogin }) {
  const [name, setName] = useState('');
  const [pin, setPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        name,
        pin,
      });

      onLogin(response.data.user, response.data.token);
      toast.success(`Selamat datang, ${response.data.user.name}!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-[#0a1128]"
        style={{
          backgroundImage:
            "url('https://lh3.googleusercontent.com/d/1yImaTwcW1ocM-gX7wIaCjUJHGj8wYCye=w2000')",
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      />

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#09090b] relative">
        <div className="absolute top-4 right-4">
          <ThemeToggle />
        </div>
        <div className="w-full max-w-md space-y-8">
          <div className="space-y-2">
            <h2 className="font-heading text-4xl font-bold text-white">Log Masuk</h2>
            <p className="text-zinc-400">Pilih nama anda dan masukkan PIN</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-zinc-300">
                Nama
              </Label>
              <Select value={name} onValueChange={setName} required>
                <SelectTrigger 
                  data-testid="login-name-select"
                  className="bg-zinc-900 border-zinc-700 text-white hover:bg-zinc-800"
                >
                  <SelectValue placeholder="Pilih nama anda" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700 z-50">
                  {USERS.map((user) => (
                    <SelectItem 
                      key={user} 
                      value={user} 
                      className="text-white hover:bg-zinc-800 focus:bg-zinc-800 cursor-pointer"
                    >
                      {user}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="pin" className="text-zinc-300">
                PIN
              </Label>
              <div className="relative">
                <Input
                  id="pin"
                  data-testid="login-pin-input"
                  type={showPin ? 'text' : 'password'}
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  required
                  className="bg-transparent border-b border-zinc-700 focus:border-[#DFFF00] focus:ring-0 rounded-none px-0 pr-10 text-lg font-mono text-white placeholder:text-zinc-600"
                  placeholder="••••"
                  maxLength={6}
                />
                <button
                  type="button"
                  data-testid="login-pin-toggle"
                  onClick={() => setShowPin((v) => !v)}
                  aria-label={showPin ? 'Sembunyikan PIN' : 'Tunjukkan PIN'}
                  className="absolute right-0 top-1/2 -translate-y-1/2 p-1 text-zinc-500 hover:text-[#DFFF00] transition-colors focus:outline-none"
                >
                  {showPin ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading || !name}
              className="w-full bg-[#DFFF00] hover:bg-[#DFFF00]/90 text-black font-semibold uppercase tracking-widest py-6 rounded-sm transition-transform hover:-translate-y-0.5"
            >
              {loading ? 'Sedang log masuk...' : 'Log Masuk'}
            </Button>
          </form>

          <div className="pt-4 border-t border-zinc-800">
            <p className="text-xs text-zinc-500 text-center">
              Semua pengguna menggunakan PIN yang ditetapkan
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
